import { useEffect, useState } from "react";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, UnstyledButton } from "@mantine/core";
import { useResponsive } from "hooks/useResponsive";
import { useTranslation } from "react-i18next";
import classes from "./BottomToggle.module.css";

const TOGGLE_LENGTH = 56; // px, also in BottomToggle.module.css
const ICON_TOP_PADDING = 2; // px
const OPEN_CLOSE_ANIMATION_DURATION = 400; // ms, also in BottomToggle.module.css

type Props = {
  handleToggle: (e: React.MouseEvent<HTMLElement>) => void;
  isOpen: boolean;
  zIndex?: number;
};

export const BottomToggle = ({ handleToggle, isOpen, zIndex }: Props) => {
  const { isSm } = useResponsive();
  const { t } = useTranslation();
  const [isFullyClosed, setIsFullyClosed] = useState(false);

  useEffect(() => {
    if (isOpen) return setIsFullyClosed(false);

    const timeout = setTimeout(() => {
      setIsFullyClosed(!isOpen);
    }, OPEN_CLOSE_ANIMATION_DURATION);

    return () => {
      clearTimeout(timeout);
    };
  }, [isOpen]);

  return (
    <UnstyledButton
      onClick={handleToggle}
      aria-label={t(
        isOpen ? "chart_panel.hide_label" : "chart_panel.show_label"
      )}
      aria-expanded={isOpen}
      ml={-(TOGGLE_LENGTH / 2)}
      pt={ICON_TOP_PADDING}
      className={classes.toggle}
      mod={{ open: isOpen, "fully-closed": isFullyClosed }}
      style={{ "--toggle-z-index": zIndex ?? 0 }}
    >
      {!isSm && (
        // touch thumb on small devices
        <Box
          onClick={handleToggle}
          style={{
            position: "absolute",
            top: -15,
            bottom: 0,
            right: -15,
            left: -15,
            borderRadius: 0,
          }}
        />
      )}
      <Stack
        style={{
          transform: isOpen ? "" : "rotate(180deg)",
          transition: "transform 0.5s",
        }}
      >
        <FontAwesomeIcon icon={faChevronDown} />
      </Stack>
    </UnstyledButton>
  );
};
